import { useMutation, useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import React, { FC, useEffect, useState, useCallback } from 'react';
import styled from '@emotion/styled';
import { TransparentButton, Button, Box, BlueButton, RedButton } from '~Styles';
import Icon from '~ComponentsImproved/Icon';
import Loader from '~ComponentsImproved/Loader';
import Modal from '~ComponentsImproved/Modal';
import useModal from '~ComponentsImproved/Modal/useModal';
import LoginModal from '~Scenes/Tables/Components/LoginModal';
import PauseIcon from '~Assets/icons/pause_circle.svg';
import {
  useButton,
  useDispatch,
  useI18n,
  useLogger,
  useSelector,
  useStaffCan,
  useWindowSize,
} from '~Hooks';
import { addToast } from '~actions';
import PauseOptionButton from './PauseOptionButton';
import ModalWrapper from '~ComponentsImproved/Modal/ModalWrapper';
import {
  GetPausedOrdersUntil,
  GetPausedOrdersUntilVariables,
  UpdateVenueMetadata,
  UpdateVenueMetadataVariables,
} from './__queries__';
import DividerWithText from '~Components/DividerWithText';
import TimeManager from '~Components/TimeManager';
import { STAFF_PERMISSIONS } from '../../../../../../__queries__';

const PAUSED_UNTIL_QUERY = gql`
  query GetPausedOrdersUntil($venueId: ID) {
    Venue(id: $venueId) {
      pauseOnlineOrdersUntil
    }
  }
`;

const PAUSE_ONLINE_ORDERS = gql`
  mutation UpdateVenueMetadata($input: UpdateVenueMetadataInput!) {
    updateVenueMetadata(input: $input) {
      success
      error
      message
    }
  }
`;

const OptionsGrid = styled(Box)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`;

const CancelButton = styled(Button)`
  max-height: 40px;
  min-width: 100px;
`;

const ConfirmButton = styled(BlueButton)`
  max-height: 40px;
  white-space: nowrap;
`;

const ResumeButton = styled(RedButton)`
  max-height: 40px;
  white-space: nowrap;
`;

const OpenModalButton = styled(TransparentButton)<{ isPaused: boolean; active?: boolean }>`
  white-space: nowrap;
  color: ${({ isPaused, active }) =>
    isPaused ? 'var(--colorUIYellow)' : active ? 'var(--colorWhite)' : 'var(--colorBlueGray200)'};
  background: ${({ active }) => (active ? 'var(--colorBlueGray1000)' : 'transparent')};
`;

interface PauseOption {
  labelKey: string;
  minutes: number;
  confirmTextKey: string;
}

const PAUSE_OPTIONS: PauseOption[] = [
  {
    labelKey: 'KDS.PauseOnlineOrders.15Min',
    minutes: 15,
    confirmTextKey: 'KDS.PauseOnlineOrders.15Min',
  },
  {
    labelKey: 'KDS.PauseOnlineOrders.30Min',
    minutes: 30,
    confirmTextKey: 'KDS.PauseOnlineOrders.30Min',
  },
  {
    labelKey: 'KDS.PauseOnlineOrders.1Hour',
    minutes: 60,
    confirmTextKey: 'KDS.PauseOnlineOrders.1Hour',
  },
  {
    labelKey: 'KDS.PauseOnlineOrders.AllDay',
    minutes: -1,
    confirmTextKey: 'KDS.PauseOnlineOrders.RestOfTheDay',
  },
];

// Calculate ISO timestamp for when the pause should end
const getPauseUntilDate = (minutes: number): string => {
  if (minutes === -1) {
    // Rest of the day — pause until midnight
    const endOfDay = new Date();
    endOfDay.setHours(23, 59, 59, 999);
    return endOfDay.toISOString();
  }

  const date = new Date();
  date.setMinutes(date.getMinutes() + minutes);
  return date.toISOString();
};

/**
 * Allows kitchen staff to temporarily pause incoming online orders for a selected duration.
 * Presents a modal with preset duration options (15 min, 30 min, 1 hour, rest of the day)
 * in a 2-column grid. The confirm button remains disabled until a duration is selected.
 * Applies the pause via the UpdateVenueMetadata mutation.
 */
const PauseOnlineOrders: FC = () => {
  const { i18n } = useI18n();
  const modal = useModal();
  const logger = useLogger('pause-online-orders');
  const dispatch = useDispatch();
  const windowSize = useWindowSize();
  const staffCan = useStaffCan();

  const [pauseLoading, setPauseLoading] = useState<boolean>(false);
  const [resumeLoading, setResumeLoading] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<PauseOption | null>(null);
  const [showLogin, setShowLogin] = useState(false);
  const [shouldCheckPermission, setShouldCheckPermission] = useState(false);

  const venueId = useSelector((state) => state.venueId) as string;
  const hasStaffCodes = useSelector((state) => !!state.companyStaff?.staffs?.length);

  const pausedOrdersUntilQuery = useQuery<GetPausedOrdersUntil, GetPausedOrdersUntilVariables>(
    PAUSED_UNTIL_QUERY,
    {
      variables: { venueId },
      skip: !venueId,
      fetchPolicy: 'network-only', // Always fetch the latest pause status
    },
  );

  // Check if online orders are currently paused
  const pausedUntilRaw = pausedOrdersUntilQuery.data?.Venue?.pauseOnlineOrdersUntil;
  const isPaused = Boolean(pausedUntilRaw && new Date(pausedUntilRaw) > new Date());
  const queryLoading = pausedOrdersUntilQuery.loading;
  const queryFailed =
    Boolean(pausedOrdersUntilQuery.error) ||
    (!pausedOrdersUntilQuery.loading && !pausedOrdersUntilQuery.data);

  const showModal = useCallback(() => {
    // If the query failed, retry instead of opening the modal
    if (queryFailed) {
      pausedOrdersUntilQuery.refetch();
      return;
    }

    modal.show();
  }, [queryFailed, pausedOrdersUntilQuery, modal]);

  const hasPermission = staffCan.multi([STAFF_PERMISSIONS.ASSUME_ROLE]);

  useEffect(() => {
    if (!shouldCheckPermission) {
      return;
    }

    if (hasPermission) {
      showModal();
    } else {
      dispatch(addToast(i18n('KDS.PauseOnlineOrders.NoPermission'), true));
    }

    setShouldCheckPermission(false);
  }, [shouldCheckPermission, hasPermission, showModal, dispatch]);

  const openModalButton = useButton({
    onClick: () => {
      if (hasStaffCodes) {
        setShowLogin(true);
      } else {
        showModal();
      }
    },
  });

  const [pauseOnlineOrders] = useMutation<UpdateVenueMetadata, UpdateVenueMetadataVariables>(
    PAUSE_ONLINE_ORDERS,
  );

  const cancelButton = useButton({
    onClick: () => {
      setSelectedOption(null);
      modal.hide();
    },
  });

  const confirmButton = useButton({
    onClick: () => handleConfirm(),
    disabled: !selectedOption || pauseLoading || resumeLoading,
  });

  const resumeButton = useButton({
    onClick: () => handleResume(),
    disabled: pauseLoading || resumeLoading,
  });

  // Select a pause duration
  const handleSelectOption = (option: PauseOption) => {
    logger.info('Pause duration selected', { duration: option.labelKey });
    setSelectedOption(option);
  };

  // Remove the active pause by setting pauseOnlineOrdersUntil to now
  const handleResume = async () => {
    if (!venueId) return;

    logger.info('Resuming online orders');
    setResumeLoading(true);

    try {
      const result = await pauseOnlineOrders({
        variables: {
          input: {
            venueId,
            pauseOnlineOrdersUntil: TimeManager.now().toISOString(),
          },
        },
      });

      if (result.data?.updateVenueMetadata.success) {
        logger.info('Online orders resumed successfully');
        dispatch(addToast(i18n('KDS.PauseOnlineOrders.Resumed'), true));
        pausedOrdersUntilQuery.refetch();
        setSelectedOption(null);
        modal.hide();
      } else {
        logger.error('Failed to resume online orders', {
          errorMsg: result.data?.updateVenueMetadata.error,
        });
        dispatch(addToast(i18n('KDS.PauseOnlineOrders.ResumeFailed'), true, null, true));
      }
    } catch (error) {
      logger.error('Error resuming online orders', { errorData: { error } });
      dispatch(addToast(i18n('KDS.PauseOnlineOrders.ResumeFailed'), true, null, true));
    }

    setResumeLoading(false);
  };

  // Execute the pause mutation
  const handleConfirm = async () => {
    if (!selectedOption || !venueId) {
      logger.error('Missing data for pause', { hasOption: !!selectedOption, venueId });
      return;
    }

    const pauseUntil = getPauseUntilDate(selectedOption.minutes);
    logger.info('Pausing online orders', { duration: selectedOption.labelKey, pauseUntil });

    setPauseLoading(true);

    try {
      const result = await pauseOnlineOrders({
        variables: {
          input: {
            venueId,
            pauseOnlineOrdersUntil: pauseUntil,
          },
        },
      });

      if (result.data?.updateVenueMetadata.success) {
        logger.info('Online orders paused successfully', { pauseUntil });
        dispatch(
          addToast(
            i18n('KDS.PauseOnlineOrders.PausedFor', i18n(selectedOption.confirmTextKey)),
            true,
          ),
        );
        pausedOrdersUntilQuery.refetch();
        setSelectedOption(null);
        modal.hide();
      } else {
        logger.error('Failed to pause online orders', {
          errorMsg: result.data?.updateVenueMetadata.error,
        });
        dispatch(addToast(i18n('KDS.PauseOnlineOrders.PauseFailed'), true, null, true));
      }
    } catch (error) {
      logger.error('Error pausing online orders', { errorData: { error } });
      dispatch(addToast(i18n('KDS.PauseOnlineOrders.PauseFailed'), true, null, true));
    }

    setPauseLoading(false);
  };

  return (
    <>
      {showLogin && (
        <LoginModal
          onSuccess={() => setShouldCheckPermission(true)}
          onClose={() => setShowLogin(false)}
        />
      )}
      <OpenModalButton
        display="flex"
        alignItems="center"
        size="large"
        gap="xs"
        {...openModalButton}
        active={modal.isShowing}
        isPaused={isPaused}>
        {queryLoading ? (
          <Loader size="sm" color="var(--colorBlueGray200)" />
        ) : (
          <>
            <Icon
              src={PauseIcon}
              color={isPaused ? 'var(--colorUIYellow)' : 'var(--colorBlueGray200)'}
              height="24px"
              width="24px"
            />
            {windowSize.width > 1080 && (
              <>
                {queryFailed
                  ? i18n('KDS.PauseOnlineOrders.FetchFailed')
                  : isPaused
                  ? i18n(
                      'KDS.PauseOnlineOrders.PausedUntil',
                      new Date(pausedUntilRaw!).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      }),
                    )
                  : i18n('KDS.PauseOnlineOrders.PauseOnline')}
              </>
            )}
          </>
        )}
      </OpenModalButton>

      <Modal
        isShowing={modal.isShowing}
        hide={() => {
          setSelectedOption(null);
          modal.hide();
        }}
        backdropClose>
        <ModalWrapper
          header={{
            title: isPaused
              ? i18n(
                  'KDS.PauseOnlineOrders.TitlePaused',
                  new Date(pausedUntilRaw!).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  }),
                )
              : i18n('KDS.PauseOnlineOrders.Title'),
            subtitle: isPaused
              ? i18n('KDS.PauseOnlineOrders.SubtitlePaused')
              : i18n('KDS.PauseOnlineOrders.Subtitle'),
            icon: PauseIcon,
          }}
          padding="medium">
          {pausedOrdersUntilQuery.loading ? (
            <Loader size="sm" color="var(--colorBlueGray200)" />
          ) : (
            <>
              {isPaused && (
                <>
                  <ResumeButton fluid {...resumeButton}>
                    {resumeLoading ? (
                      <Loader size="sm" color="var(--colorWhite)" />
                    ) : (
                      i18n('KDS.PauseOnlineOrders.ResumeOnlineOrders')
                    )}
                  </ResumeButton>

                  <DividerWithText />
                </>
              )}

              <OptionsGrid fluid>
                {PAUSE_OPTIONS.map((option) => (
                  <PauseOptionButton
                    key={option.minutes}
                    label={i18n(option.labelKey)}
                    isSelected={selectedOption?.minutes === option.minutes}
                    onSelect={() => handleSelectOption(option)}
                  />
                ))}
              </OptionsGrid>

              <Box
                display="flex"
                flexDirection="row"
                gap="xs"
                alignItems="center"
                fluid
                marginTop="small">
                <CancelButton {...cancelButton}>{i18n('POS.Cancel')}</CancelButton>
                <ConfirmButton fluid {...confirmButton}>
                  {pauseLoading ? (
                    <Loader size="sm" color="var(--colorWhite)" />
                  ) : isPaused ? (
                    i18n('KDS.PauseOnlineOrders.ExtendPause')
                  ) : (
                    i18n('KDS.PauseOnlineOrders.PauseOrders')
                  )}
                </ConfirmButton>
              </Box>
            </>
          )}
        </ModalWrapper>
      </Modal>
    </>
  );
};

export default PauseOnlineOrders;
